import React, { useState } from "react";
import { Modal } from "./Modal";
import { Select } from "../select/Select";
import { useCreateItem } from "../../hooks/useItems";
import { useToastStore } from "../../stores/toastStore";
import { formatCurrency, parseCurrency, getCurrencySymbol } from "../../lib/currencyUtils";

interface ManualAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  url: string;
  defaultListId?: string | null;
  initialData?: {
    title?: string;
    description?: string;
    price?: number;
    currency?: string;
    image?: string;
    merchant?: string;
  };
}

export const ManualAddModal: React.FC<ManualAddModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  url,
  defaultListId,
  initialData
}) => {
  const createItemMutation = useCreateItem();
  const showToast = useToastStore((s) => s.show);

  const [name, setName] = useState(initialData?.title || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [price, setPrice] = useState("");
  const [displayPrice, setDisplayPrice] = useState("");
  const [currency, setCurrency] = useState(initialData?.currency || "USD");
  const [imageUrl, setImageUrl] = useState(initialData?.image || "");
  const [merchant, setMerchant] = useState(initialData?.merchant || "");
  const [loading, setLoading] = useState(false);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Remove all non-numeric characters except decimal point
    const cleaned = inputValue.replace(/[^\d.]/g, "");
    
    // Prevent multiple decimal points
    const parts = cleaned.split(".");
    let normalized = parts[0];
    if (parts.length > 1) {
      normalized += "." + parts.slice(1).join("").substring(0, 2); // Max 2 decimal places
    }
    
    // Update the raw price value (for submission)
    setPrice(normalized);
    
    // Update the displayed price (formatted with commas)
    if (normalized === "" || normalized === ".") {
      setDisplayPrice("");
    } else {
      setDisplayPrice(formatCurrency(normalized));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    try {
      const parsedPrice = price ? parseFloat(parseCurrency(price)) : undefined;
      await createItemMutation.mutateAsync({
        listId: defaultListId || null,
        name: name.trim(),
        url,
        description: description.trim() || undefined,
        price: parsedPrice,
        currency: currency || "USD",
        imageUrl: imageUrl.trim() || undefined,
        merchantName: merchant.trim() || undefined,
        brandName: merchant.trim() || undefined
      });

      showToast({
        type: "success",
        title: "Item added"
      });
      
      // Reset form
      setName("");
      setDescription("");
      setPrice("");
      setDisplayPrice("");
      setCurrency("USD");
      setImageUrl("");
      setMerchant("");
      
      onClose();
      onSuccess?.();
    } catch (error) {
      showToast({
        type: "error",
        title: "Failed to add item",
        description: "Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <>
      <button
        type="button"
        className="ob-button ob-button-ghost"
        onClick={onClose}
        disabled={loading}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="ob-button ob-button-primary"
        onClick={handleSubmit}
        disabled={loading || !name.trim()}
      >
        {loading ? "Adding..." : "Manually Add"}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manually Add Item"
      subtitle="We're struggling to pull data. You can manually add it."
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="ob-modal-form">
        <div className="ob-field">
          <label htmlFor="manual-add-name">Name *</label>
          <input
            id="manual-add-name"
            className="ob-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Item name"
            required
            disabled={loading}
            autoFocus
          />
        </div>

        <div className="ob-field">
          <label htmlFor="manual-add-description">Description (Optional)</label>
          <textarea
            id="manual-add-description"
            className="ob-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description"
            rows={3}
            disabled={loading}
          />
        </div>

        <div className="ob-field">
          <label htmlFor="manual-add-price">Price (Optional)</label>
          <div className="ob-price-input-group">
            <Select
              id="manual-add-currency"
              options={[
                { value: "USD", label: `${getCurrencySymbol("USD")} USD`, displayValue: getCurrencySymbol("USD") },
                { value: "EUR", label: `${getCurrencySymbol("EUR")} EUR`, displayValue: getCurrencySymbol("EUR") },
                { value: "GBP", label: `${getCurrencySymbol("GBP")} GBP`, displayValue: getCurrencySymbol("GBP") },
                { value: "CAD", label: `${getCurrencySymbol("CAD")} CAD`, displayValue: getCurrencySymbol("CAD") }
              ]}
              value={currency}
              onChange={(value) => setCurrency(value)}
              disabled={loading}
            />
            <input
              id="manual-add-price"
              type="text"
              inputMode="decimal"
              className="ob-input"
              value={displayPrice}
              onChange={handlePriceChange}
              placeholder="0.00"
              disabled={loading}
            />
          </div>
        </div>

        <div className="ob-field">
          <label htmlFor="manual-add-image">Image URL (Optional)</label>
          <input
            id="manual-add-image"
            className="ob-input"
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            disabled={loading}
          />
        </div>

        <div className="ob-field">
          <label htmlFor="manual-add-merchant">Merchant/Brand (Optional)</label>
          <input
            id="manual-add-merchant"
            className="ob-input"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            placeholder="Store or brand name"
            disabled={loading}
          />
        </div>

        <div className="ob-field">
          <label htmlFor="manual-add-url">URL</label>
          <input
            id="manual-add-url"
            className="ob-input"
            value={url}
            disabled
            readOnly
          />
        </div>
      </form>
    </Modal>
  );
};
